import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { LabApiError, labApi } from './api'
import { CopyButton, FrameStrip, HeroMedia, Leaderboard, Loading, PelicanMedia, Plate, ResultCard, ResultGrid, SceneMedia, ScoreMeter, SectionHead, ShareButtons } from './components'
import './detail.css'
import { badgeOf, formatCost, formatScore, formatSeconds, isLabMediaUrl, modelFromSlug, modelLabel, modelPath, modelSlug, PELICAN_PATH, resultPath, scenePath, testerPath } from './format'
import { SCENES, sceneApi, type SceneSlug } from './scenes'
import { BrowseHub, ModelProfilePage } from './browse'
import { FaceOff, GuessGame } from './games'
import { PelicanArt } from './art'
import { RunPanel } from './RunPanel'
import { PelicanLandingView } from './PelicanLanding'
import { useT } from './i18n'
import type { LabResult, LabRun, LabSummary } from './types'

type Async<T> = { data: T | null; error: LabApiError | null; loading: boolean }

function useLabData<T>(path: string | null): Async<T> {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: Boolean(path) })
  useEffect(() => {
    if (!path) return
    let cancelled = false
    setState({ data: null, error: null, loading: true })
    labApi<T>(path)
      .then((data) => { if (!cancelled) setState({ data, error: null, loading: false }) })
      .catch((error: unknown) => {
        if (!cancelled) setState({ data: null, error: error instanceof LabApiError ? error : new LabApiError(0, 'Network error.'), loading: false })
      })
    return () => { cancelled = true }
  }, [path])
  return state
}

const useSummary = (scene: SceneSlug = 'pelican-test') => useLabData<LabSummary>(sceneApi(scene))

function useTitle(title: string) {
  useEffect(() => { document.title = title }, [title])
}

function ErrorBlock({ title, message, scene = 'pelican-test' }: { title: string; message: string; scene?: SceneSlug }) {
  const t = useT()
  return (
    <div className="measure pad-xl">
      <h1>{title}</h1>
      <p className="lede muted">{message}</p>
      <Link to={scenePath(scene)} className="btn btn-ink">{t.nav.backTo(SCENES[scene].name)}</Link>
    </div>
  )
}

// ─── /lab ────────────────────────────────────────────────────

const SCENE_CARDS = [
  // Taglines, descriptions and tags live in i18n (t.labIndex.cards[slug]).
  { slug: 'pelican-test' as SceneSlug, name: 'The Pelican Test', href: PELICAN_PATH, live: true },
  { slug: 'endless-runner' as SceneSlug, name: 'Endless Runner', href: scenePath('endless-runner'), live: true },
  { slug: 'watermelon-test' as SceneSlug, name: 'The Watermelon Test', href: scenePath('watermelon-test'), live: true },
]

export function LabIndex() {
  const t = useT()
  useTitle(t.labIndex.title)
  const { data: pelican } = useSummary('pelican-test')
  const { data: runner } = useSummary('endless-runner')
  const { data: watermelon } = useSummary('watermelon-test')
  const covers: Record<SceneSlug, LabResult | undefined> = {
    'pelican-test': pelican?.featured.find((r) => isLabMediaUrl(r.png)),
    'endless-runner': runner?.featured.find((r) => isLabMediaUrl(r.html)),
    'watermelon-test': watermelon?.featured.find((r) => isLabMediaUrl(r.png)),
  }
  return (
    <>
      <section className="hero hero-plain">
        <div className="measure">
          <p className="eyebrow">{t.labIndex.eyebrow}</p>
          <h1 className="display">{t.labIndex.displayLead} <mark>{t.labIndex.displayMark}</mark></h1>
          <p className="lede">{t.labIndex.lede}</p>
        </div>
      </section>
      <section className="section" aria-labelledby="scenes-title">
        <div className="measure">
          <SectionHead id="scenes-title" eyebrow={t.labIndex.sectionEyebrow} title={t.labIndex.sectionTitle} />
          <div className="grid-2">
            {SCENE_CARDS.map((scene) => {
              const cover = covers[scene.slug]
              const copy = t.labIndex.cards[scene.slug]
              const body = (
                <>
                  <div className="scene-cover">
                    {scene.slug === 'pelican-test'
                      ? (cover?.png ? <img src={cover.png} alt={t.labIndex.altPelican} /> : <PelicanArt animated={false} />)
                      : scene.slug === 'watermelon-test' && cover?.png
                        ? <img src={cover.png} alt={t.labIndex.altWatermelon} />
                        : (cover ? <span className="eyebrow">{t.labIndex.playableInside}</span> : <span className="eyebrow">{t.labIndex.comingSoon}</span>)}
                  </div>
                  <div className="card-body">
                    <p className="eyebrow accent">{t.labIndex.live} · {copy.tags.join(' · ')}</p>
                    <h3 className="card-title">{scene.name}</h3>
                    <p>{copy.tagline}</p>
                    <p className="small muted">{copy.description}</p>
                    <span className="eyebrow accent">{t.labIndex.playNow}</span>
                  </div>
                </>
              )
              return <Link key={scene.slug} to={scene.href} className="card scene-card">{body}</Link>
            })}
          </div>
        </div>
      </section>
    </>
  )
}

// ─── /lab/pelican-test ───────────────────────────────────────

export function PelicanLanding() {
  const t = useT()
  const c = t.pelican
  useTitle(c.title)
  const { data: summary, error, loading } = useSummary()
  if (loading) return <Loading label={c.loading} />
  if (!summary) return <ErrorBlock title={c.errorTitle} message={error?.message ?? t.common.retryMinute} />
  return <PelicanLandingView summary={summary} />
}

// ─── /lab/endless-runner ───────────────────────────────────

export function RunnerLanding() {
  const t = useT()
  const c = t.runner
  useTitle(c.title)
  const { data: summary, error, loading } = useSummary('endless-runner')
  const justTested = useMemo(() => new Set(summary?.just_tested.map((r) => r.model) ?? []), [summary])
  const suggested = useMemo(() => summary?.leaderboard.map((r) => r.model) ?? [], [summary])
  const { hash } = useLocation()
  useEffect(() => {
    if (summary && hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView()
  }, [summary, hash])

  if (loading) return <Loading label={c.loading} />
  if (!summary) return <ErrorBlock scene="endless-runner" title={c.errorTitle} message={error?.message ?? t.common.retryMinute} />

  const hasResults = summary.stats.results > 0
  // Featured is sorted best first: the hero plays the top-scoring game.
  const top = summary.featured.find((r) => isLabMediaUrl(r.html))
  const stats: Array<[string, string]> = [
    [String(summary.stats.models), c.statModels],
    [String(summary.stats.results), c.statResults],
    [String(summary.stats.testers), c.statTesters],
    [formatCost(summary.stats.avg_cost), c.statCost],
  ]
  const steps: Array<[string, string, string]> = c.steps.map(([n, title, d]) => [n, title, d || c.judgedBy(modelLabel(summary.run_limits.judge_model))])

  return (
    <>
      <section className="hero" aria-labelledby="runner-title">
        <div className="measure hero-grid">
          <div>
            <p className="eyebrow">{c.heroEyebrow}</p>
            <h1 id="runner-title" className="display">{c.heroLead}<mark>{c.heroMark}</mark></h1>
            <p className="lede">{c.lede}</p>
            <div className="actions">
              <a href="#run" className="btn btn-lime">{t.landing.runYourOwn}</a>
              {hasResults ? <a href="#leaderboard" className="btn btn-ghost">{t.landing.seeLeaderboard}</a> : <a href="#how" className="btn btn-ghost">{t.landing.howItWorks}</a>}
            </div>
            {hasResults ? (
              <dl className="stats">
                {stats.map(([value, label]) => (
                  <div key={label}><dt className="sr-only">{label}</dt><dd className="stat-value num">{value}</dd><dd className="eyebrow">{label}</dd></div>
                ))}
              </dl>
            ) : null}
          </div>
          <div className="hero-art">
            <HeroMedia result={top} scene="endless-runner" empty={hasResults ? c.emptyPlayable : c.emptyNoGames} />
          </div>
        </div>
      </section>

      {summary.just_tested.length ? (
        <section className="ticker">
          <div className="measure row-wrap">
            <span className="pill">{t.landing.justTested}</span>
            {summary.just_tested.map((r) => (
              <Link key={r.model} to={modelPath(r.model, 'endless-runner')} className="strong">
                {modelLabel(r.model)} <span className="mono small subtle">{r.score != null ? t.common.score5(r.score) : t.common.noScore}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {hasResults ? (
        <>
          <section id="leaderboard" className="section" aria-labelledby="board-title">
            <div className="measure">
              <SectionHead id="board-title" eyebrow={t.landing.leaderboardEyebrow} title={c.boardTitle} lede={c.boardLede} />
              <Leaderboard rows={summary.leaderboard} justTested={justTested} scene="endless-runner" />
            </div>
          </section>

          {summary.featured.length ? (
            <section className="section tint" aria-labelledby="fame-title">
              <div className="measure">
                <SectionHead id="fame-title" eyebrow={t.landing.fameEyebrow} title={c.fameTitle} lede={c.fameLede} />
                <ResultGrid items={summary.featured} scene="endless-runner" />
              </div>
            </section>
          ) : null}

          {summary.flops.length ? (
            <section className="section" aria-labelledby="shame-title">
              <div className="measure">
                <SectionHead id="shame-title" eyebrow={t.landing.shameEyebrow} title={c.shameTitle} lede={c.shameLede} />
                <ResultGrid items={summary.flops} scene="endless-runner" />
              </div>
            </section>
          ) : null}

          <section id="testers" className="section tint" aria-labelledby="browse-title">
            <div className="measure">
              <SectionHead id="browse-title" eyebrow={t.landing.browseEyebrow} title={c.browseTitle} lede={c.browseLede} />
              <BrowseHub summary={summary} scene="endless-runner" />
            </div>
          </section>
        </>
      ) : (
        <section id="how" className="section" aria-labelledby="how-title">
          <div className="measure">
            <SectionHead id="how-title" eyebrow={t.landing.howItWorks} title={c.howTitle} lede={c.howLede} />
            <div className="steps-grid">
              {steps.map(([n, title, d]) => <div key={n} className="step"><b>{n}</b><h3>{title}</h3><p>{d}</p></div>)}
            </div>
          </div>
        </section>
      )}

      <section id="run" className="section dark" aria-labelledby="run-title">
        <div className="measure">
          <SectionHead id="run-title" eyebrow={t.landing.yourTurn} title={hasResults ? c.runTitleHas : c.runTitleEmpty} lede={c.runLede} />
          <RunPanel suggested={suggested} limits={summary.run_limits} defaultSubject={summary.default_subject} scene="endless-runner" />
        </div>
      </section>

      <section className="section" aria-labelledby="prompt-title">
        <div className="measure">
          <SectionHead id="prompt-title" eyebrow={t.landing.theBrief} title={t.landing.stealBrief} lede={t.landing.promptHint} />
          <div className="grid-2">
            {summary.prompts.map((p) => (
              <div key={p.kind}>
                <div className="code-panel">
                  <div className="code-head"><span>{p.title}</span><CopyButton text={p.text} /></div>
                  <pre>{p.text}</pre>
                </div>
                <p className="small muted">{p.why}</p>
              </div>
            ))}
          </div>
          <dl className="dims">
            {summary.dimensions.map((d) => (
              <div key={d.key}>
                <dt className="eyebrow accent">{d.source === 'auto' ? t.landing.measured : t.landing.judgeScale}</dt>
                <dd className="strong">{d.name}</dd>
                <dd className="small muted">{d.description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  )
}

// ─── /lab/watermelon-test ──────────────────────────────────

// How the first official results were produced lives in i18n (t.watermelon.method); keep in step with seed/watermelon-test/README.md.

export function WatermelonLanding() {
  const scene: SceneSlug = 'watermelon-test'
  const t = useT()
  const c = t.watermelon
  useTitle(c.title)
  const { data: summary, error, loading } = useSummary(scene)
  const justTested = useMemo(() => new Set(summary?.just_tested.map((r) => r.model) ?? []), [summary])
  const suggested = useMemo(() => summary?.leaderboard.map((r) => r.model) ?? [], [summary])
  const { hash } = useLocation()
  useEffect(() => {
    if (summary && hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView()
  }, [summary, hash])

  if (loading) return <Loading label={c.loading} />
  if (!summary) return <ErrorBlock scene={scene} title={c.errorTitle} message={error?.message ?? t.common.retryMinute} />

  const hasResults = summary.stats.results > 0
  const cover = summary.featured.find((r) => isLabMediaUrl(r.png))
  const userRuns = summary.run_limits.user_runs !== false
  const stats: Array<[string, string]> = [
    [String(summary.stats.models), c.statModels],
    [String(summary.stats.results), c.statResults],
    [String(summary.stats.testers), c.statTesters],
    [formatCost(summary.stats.avg_cost), c.statCost],
  ]
  const steps: Array<[string, string, string]> = c.steps.map(([n, title, d]) => [n, title, d || c.judgedBy(modelLabel(summary.run_limits.judge_model))])

  return (
    <>
      <section className="hero" aria-labelledby="watermelon-title">
        <div className="measure hero-grid">
          <div>
            <p className="eyebrow">{c.heroEyebrow}</p>
            <h1 id="watermelon-title" className="display">{c.heroLead}<mark>{c.heroMark}</mark></h1>
            <p className="lede">{c.lede}</p>
            <div className="actions">
              {hasResults ? <a href="#leaderboard" className="btn btn-lime">{t.landing.seeLeaderboard}</a> : <a href="#how" className="btn btn-lime">{t.landing.howItWorks}</a>}
              <a href="#run" className="btn btn-ghost">{userRuns ? t.landing.runYourOwn : c.runYourOwnSoon}</a>
            </div>
            {hasResults ? (
              <dl className="stats">
                {stats.map(([value, label]) => (
                  <div key={label}><dt className="sr-only">{label}</dt><dd className="stat-value num">{value}</dd><dd className="eyebrow">{label}</dd></div>
                ))}
              </dl>
            ) : null}
          </div>
          <div className="hero-art">
            <div className="scene-cover">
              {cover?.png ? <img src={cover.png} alt={c.coverAlt(modelLabel(cover.model))} /> : <span className="eyebrow">{c.screenshotsBelow}</span>}
            </div>
            <span className="sticker">{cover ? modelLabel(cover.model) : c.stickerEmpty}</span>
          </div>
        </div>
      </section>

      {summary.just_tested.length ? (
        <section className="ticker">
          <div className="measure row-wrap">
            <span className="pill">{t.landing.justTested}</span>
            {summary.just_tested.map((r) => (
              <Link key={r.model} to={modelPath(r.model, scene)} className="strong">
                {modelLabel(r.model)} <span className="mono small subtle">{r.score != null ? t.common.score5(r.score) : t.common.noScore}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {hasResults ? (
        <>
          <section id="leaderboard" className="section" aria-labelledby="board-title">
            <div className="measure">
              <SectionHead id="board-title" eyebrow={t.landing.leaderboardEyebrow} title={c.boardTitle} lede={c.boardLede} />
              <Leaderboard rows={summary.leaderboard} justTested={justTested} scene={scene} />
            </div>
          </section>

          {summary.featured.length ? (
            <section className="section tint" aria-labelledby="fame-title">
              <div className="measure">
                <SectionHead id="fame-title" eyebrow={t.landing.fameEyebrow} title={c.fameTitle} lede={c.fameLede} />
                <ResultGrid items={summary.featured} scene={scene} />
              </div>
            </section>
          ) : null}

          {summary.flops.length ? (
            <section className="section" aria-labelledby="shame-title">
              <div className="measure">
                <SectionHead id="shame-title" eyebrow={t.landing.shameEyebrow} title={c.shameTitle} lede={c.shameLede} />
                <ResultGrid items={summary.flops} scene={scene} />
              </div>
            </section>
          ) : null}

          <section id="testers" className="section tint" aria-labelledby="browse-title">
            <div className="measure">
              <SectionHead id="browse-title" eyebrow={t.landing.browseEyebrow} title={c.browseTitle} lede={c.browseLede} />
              <BrowseHub summary={summary} scene={scene} />
            </div>
          </section>
        </>
      ) : null}

      <section id="how" className="section" aria-labelledby="how-title">
        <div className="measure">
          <SectionHead id="how-title" eyebrow={c.methodEyebrow} title={c.howTitle} lede={c.method} />
          <div className="steps-grid">
            {steps.map(([n, title, d]) => <div key={n} className="step"><b>{n}</b><h3>{title}</h3><p>{d}</p></div>)}
          </div>
        </div>
      </section>

      <section id="run" className="section dark" aria-labelledby="run-title">
        <div className="measure">
          <SectionHead id="run-title" eyebrow={t.landing.yourTurn} title={c.runTitle} lede={userRuns ? c.runLedeHas : c.runLedeOfficial} />
          <RunPanel suggested={suggested} limits={summary.run_limits} defaultSubject={summary.default_subject} scene={scene} />
        </div>
      </section>

      <section className="section" aria-labelledby="prompt-title">
        <div className="measure">
          <SectionHead id="prompt-title" eyebrow={t.landing.thePrompt} title={t.landing.stealPrompt} lede={t.landing.promptHint} />
          {summary.prompts.map((p) => (
            <div key={p.kind}>
              <div className="code-panel">
                <div className="code-head"><span>{p.title}</span><CopyButton text={p.text} /></div>
                <pre>{p.text}</pre>
              </div>
              <p className="small muted">{p.why}</p>
            </div>
          ))}
          <dl className="dims">
            {summary.dimensions.map((d) => (
              <div key={d.key}>
                <dt className="eyebrow accent">{d.source === 'auto' ? t.landing.measured : t.landing.judgeScale}</dt>
                <dd className="strong">{d.name}</dd>
                <dd className="small muted">{d.description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  )
}

// ─── /lab/:scene/runs/:id ────────────────────────────────────

// Step labels live in i18n (t.run.step).

export function RunPage({ scene = 'pelican-test' }: { scene?: SceneSlug }) {
  const { id = '' } = useParams()
  const info = SCENES[scene]
  const [run, setRun] = useState<LabRun | null>(null)
  const [missing, setMissing] = useState(false)
  const t = useT()
  const units = t.scene[scene].units
  useTitle(`${info.name} ${t.run.titleSuffix}`)

  const fetchRun = useCallback(async () => {
    try {
      const next = await labApi<LabRun>(`${sceneApi(scene)}/runs/${encodeURIComponent(id)}`)
      setRun(next)
      setMissing(false)
    } catch {
      setMissing(true)
    }
  }, [id, scene])

  useEffect(() => { setRun(null); void fetchRun() }, [fetchRun])
  const active = !run || run.status === 'running'
  useEffect(() => {
    if (!active || missing) return
    const timer = setInterval(() => { void fetchRun() }, 3000)
    return () => clearInterval(timer)
  }, [active, missing, fetchRun])

  if (missing && !run) return <ErrorBlock scene={scene} title={t.run.notFoundTitle} message={t.run.notFoundMsg} />
  if (!run) return <Loading label={t.run.loading(units)} />

  const done = run.results.filter((r) => r.status === 'done' || r.status === 'failed').length
  const best = [...run.results].filter((r) => r.ok).sort((a, b) => (b.score ?? -1) - (a.score ?? -1))[0]
  const title = run.status === 'succeeded' ? t.run.titleDone(units) : run.status === 'failed' ? t.run.titleFailed : t.run.titleMaking(units)
  const pct = run.results.length ? (done / run.results.length) * 100 : 0

  return (
    <div className={`dx scene-${scene}`}>
      <section className="dx-hero">
        <div className="measure dx-hero-pad">
          <nav className="dx-crumbs mono" aria-label="Breadcrumb"><a href="/lab/">Lab</a> / <Link to={scenePath(scene)}>{info.name}</Link></nav>
          <h1>{title}</h1>
          <p className="dx-lede">
            {t.run.byline(run.title, run.models.length)}<Link to={testerPath(run.author, scene)} className="dx-link">{run.author}</Link>{t.run.bylineEnd}
            {active ? t.run.activeTail(done, run.results.length) : t.run.doneTail(formatCost(run.total_cost))}
          </p>
          <div className="dx-progress" role="progressbar" aria-valuemin={0} aria-valuemax={run.results.length} aria-valuenow={done} aria-label={t.run.progressAria}>
            <div style={{ width: `${pct}%` }} />
          </div>
          <ul className="dx-steps" aria-live="polite" aria-label={t.run.stepsTitle}>
            {run.results.map((r) => (
              <li key={r.id} className="mono" data-status={r.status}>
                <span>{modelLabel(r.model)} · {t.common.kind(r.kind)}</span> <b>{t.run.step[r.status] ?? r.status}</b>
              </li>
            ))}
          </ul>
          {run.error && run.status === 'failed' ? <p className="dx-error">{run.error}</p> : null}
          {!active && best ? (
            <div className="dx-share"><ShareButtons path={resultPath(best.id, scene)} text={t.common.shareText(best, scene)} /></div>
          ) : null}
        </div>
      </section>
      <section className="dx-section">
        <div className="measure">
          <div className="dx-head"><span className="dx-chip">{t.run.resultsTitle}</span></div>
          <div className="grid-cards">
            {run.results.map((r) => <ResultCard key={r.id} result={r} showAuthor={false} scene={scene} />)}
          </div>
        </div>
      </section>
    </div>
  )
}

// ─── /lab/:scene/r/:id ───────────────────────────────────────

export function ResultPage({ scene = 'pelican-test' }: { scene?: SceneSlug }) {
  const { id = '' } = useParams()
  const info = SCENES[scene]
  const game = info.media === 'game'
  const toy = info.media === 'frames'
  const { data: r, error, loading } = useLabData<LabResult>(`${sceneApi(scene)}/results/${encodeURIComponent(id)}`)
  const { data: summary } = useSummary(scene)
  const t = useT()
  const unit = t.scene[scene].unit
  const did = game ? t.result.builtRunner : toy ? t.result.builtToy : t.result.drew(r?.classic ? t.result.pelicanOnBike : (r?.title.toLowerCase() ?? ''))
  const heading = r ? `${modelLabel(r.model)} ${r.ok ? did : t.result.wipedOut}` : unit
  useTitle(`${heading} | ${info.name}`)

  if (loading) return <Loading />
  if (!r) return <ErrorBlock scene={scene} title={t.result.notFound(unit[0].toUpperCase() + unit.slice(1))} message={error?.status === 404 ? t.result.notPublic(unit) : (error?.message ?? t.common.retryAgain)} />

  const badge = badgeOf(r)
  const dims = (summary?.dimensions ?? []).filter((d) => d.source === 'judge' && r.scores?.[d.key] != null)
  // Measured signals come from the source (scores) or, for browser-rendered scenes, from the probe (checks).
  const autoValue = (key: string) => r.scores?.[key] ?? r.checks?.[key]
  const autoDims = (summary?.dimensions ?? []).filter((d) => d.source === 'auto' && autoValue(d.key) != null)
  const facts: Array<[string, string]> = [
    [t.result.factScore, r.score != null ? `${formatScore(r.score)}/5` : '—'],
    [t.result.factCost, formatCost(r.cost)],
    ...(r.draw_cost != null && r.judge_cost != null ? [[t.result.factBuildJudge, `${formatCost(r.draw_cost)} + ${formatCost(r.judge_cost)}`] as [string, string]] : []),
    [t.result.factTime, formatSeconds(r.latency)],
    ...(info.tracksMotion ? [[t.result.factMotion, r.kind !== 'animated' ? t.result.motionNa : r.motion ? t.result.motionYes : t.result.motionNone] as [string, string]] : []),
  ]
  const row = summary?.leaderboard.find((x) => x.model === r.model)
  const judge = summary ? modelLabel(summary.run_limits.judge_model) : ''
  const seen = new Set<string>([r.id])
  const more = [...(summary?.featured ?? []), ...(summary?.pool ?? []), ...(summary?.faceoff_pool ?? [])]
    .filter((x) => x.model === r.model && !seen.has(x.id) && (seen.add(x.id), true))
    .slice(0, 4)
  const hasCard = dims.length > 0 || autoDims.length > 0 || Boolean(r.comment) || Boolean(r.error && !r.ok)
  return (
    <div className={`dx scene-${scene}`}>
      <section className="dx-hero">
        <div className="measure dx-result">
          <div className="dx-stage"><Plate><SceneMedia result={r} scene={scene} large /></Plate></div>
          <div className="dx-info">
            <nav className="dx-crumbs mono" aria-label="Breadcrumb"><a href="/lab/">Lab</a> / <Link to={scenePath(scene)}>{info.name}</Link> / <Link to={modelPath(r.model, scene)}>{modelLabel(r.model)}</Link></nav>
            <p className="eyebrow dx-kicker">{t.common.kind(r.kind)}{badge ? ` · ${t.common.badge(badge.label)}` : ''}</p>
            <h1>{heading}</h1>
            <p className="dx-lede">{t.result.testedBy}<Link to={testerPath(r.author, scene)} className="dx-link">{r.author}</Link>{t.result.sameAs(game)}</p>
            {game && r.ok ? <p className="dx-hint">{t.result.gameHint} <b>Space</b> {t.result.gameHintJump} <b>↓</b> {t.result.gameHintSlide}</p> : null}
            {toy && r.ok && isLabMediaUrl(r.html) ? <p className="dx-hint">{t.result.toyHint}</p> : null}
            <div className="dx-badges">
              {r.score != null ? <span className="dx-score"><b>{formatScore(r.score)}<small>/5</small></b></span> : null}
              {row ? <Link to={`${scenePath(scene)}#leaderboard`} className="dx-rank mono">{t.result.rankOf(row.rank)}</Link> : null}
            </div>
            <dl className="dx-stats">{facts.filter(([k]) => k !== t.result.factScore).map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="num">{v}</dd></div>)}</dl>
            <div className="actions">
              <Link to={`${scenePath(scene)}#run`} className="btn btn-lime">{t.result.runYourOwn} →</Link>
              <Link to={modelPath(r.model, scene)} className="btn dx-btn-ghost">{t.result.moreFromModel(modelLabel(r.model))}</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="dx-section">
        <div className="measure dx-cards">
          {hasCard ? (
            <div className="dx-card">
              <span className="dx-chip">{t.result.scorecard}</span>
              {judge ? <p className="small muted">{t.result.scorecardLede(judge)}</p> : null}
              {dims.length ? (
                <div className="dx-dims">
                  {dims.map((d) => {
                    const v = r.scores![d.key]
                    return (
                      <div key={d.key}>
                        <span>{d.name}</span>
                        <span className="dx-bar" aria-hidden="true"><i style={{ width: `${(v / 5) * 100}%` }} /></span>
                        <b className="num">{v}<small>/5</small></b>
                      </div>
                    )
                  })}
                </div>
              ) : null}
              {autoDims.length ? (
                <>
                  <h3 className="dx-card-sub">{t.result.checks}</h3>
                  <ul className="dx-checks">
                    {autoDims.map((d) => <li key={d.key} data-ok={autoValue(d.key) ? '' : undefined}><b aria-hidden="true">{autoValue(d.key) ? '✓' : '✕'}</b>{d.name}</li>)}
                  </ul>
                </>
              ) : null}
              {r.comment ? <blockquote className="dx-quote"><span className="eyebrow muted">{t.result.comment}</span>“{r.comment}”</blockquote> : null}
              {r.error && !r.ok ? <p className="mono small muted">{r.error}</p> : null}
            </div>
          ) : null}
          <div className="dx-card">
            <span className="dx-chip">{t.result.sourceTitle}</span>
            <div className="dx-links">
              {isLabMediaUrl(r.svg) ? <a href={r.svg} download className="btn btn-ghost">{t.result.downloadSvg}</a> : null}
              {isLabMediaUrl(r.html) ? <a href={r.html} download={`${info.unit}.html`} className="btn btn-ghost">{t.result.downloadHtml(unit)}</a> : null}
            </div>
            <ShareButtons path={resultPath(r.id, scene)} text={t.common.shareText(r, scene)} />
            <p className="small"><Link to={testerPath(r.author, scene)} className="inline-link">{t.result.moreFromAuthor(r.author)} →</Link></p>
          </div>
        </div>
      </section>

      {toy && r.frames?.length ? (
        <section className="dx-section tint">
          <div className="measure">
            <SectionHead eyebrow={t.result.framesEyebrow} title={t.result.framesTitle} />
            <FrameStrip result={r} />
          </div>
        </section>
      ) : null}

      {more.length ? (
        <section className="dx-section tint">
          <div className="measure">
            <div className="dx-head dx-head-row">
              <h2>{t.result.moreTitle(modelLabel(r.model))}</h2>
              <Link to={modelPath(r.model, scene)} className="link-btn">{t.result.seeAll} →</Link>
            </div>
            <ResultGrid items={more} scene={scene} />
          </div>
        </section>
      ) : null}
    </div>
  )
}

// ─── /lab/:scene/m/:slug ─────────────────────────────────────

export function ModelPage({ scene = 'pelican-test' }: { scene?: SceneSlug }) {
  const { slug = '' } = useParams()
  const { data: summary, loading } = useSummary(scene)
  const t = useT()
  const model = useMemo(() => {
    if (!summary) return null
    return summary.leaderboard.find((row) => modelSlug(row.model) === slug)?.model ?? modelFromSlug(slug)
  }, [summary, slug])
  if (loading) return <Loading />
  if (!model) return <ErrorBlock scene={scene} title={t.model.notFoundTitle} message={t.model.notFoundMsg} />
  return <ModelProfilePage summary={summary} model={model} scene={scene} />
}

export function NotFound() {
  const t = useT()
  useTitle(t.nav.notFoundTitle)
  return <ErrorBlock title={t.nav.pageNotFound} message={t.nav.pageNotFoundMsg} />
}
